<template>
  <el-dialog
    title="VISUALIZACIÓN DE PAQUETE"
    width="80%"
    :visible="visible"
    :close-on-click-modal="false"
    @open="onOpen"
    @opened="onOpened"
    @close="$emit('update:visible', false)"
    @closed="onClosed">
    <div v-loading="loading" class="box-viewer">
      <div v-if="error" class="box-error">
        <div>{{ error }}</div>
        <pre v-if="rawResponse" class="box-raw">{{ rawJson }}</pre>
      </div>
      <template v-else>
        <div v-if="packages.length > 1" class="box-selector">
          <span class="key-style">LPN</span>
          <el-select v-model="selectedIndex" size="mini" @change="renderPackage">
            <el-option
              v-for="(item, index) in packages"
              :key="`${item.lpn}-${index}`"
              :label="item.lpn || `Paquete ${index + 1}`"
              :value="index"/>
          </el-select>
        </div>
        <div class="box-content">
          <div class="box-canvas-wrapper">
            <canvas ref="canvas" class="box-canvas"></canvas>
            <div class="box-hint">Arrastra para rotar · scroll para acercar</div>
          </div>
          <div class="box-panel">
            <div class="titulo">DATOS DEL PAQUETE</div>
            <div class="box-info-style">
              <div class="value-style">{{ currentPackage.lpn || '---' }}</div>
              <div class="key-style">LPN</div>
            </div>
            <div class="box-info-style">
              <div class="value-style">{{ currentPackage.suborder || '---' }}</div>
              <div class="key-style">SUB ORDEN</div>
            </div>
            <div class="box-info-style">
              <div class="value-style">{{ boxDimensions }}</div>
              <div class="key-style">CAJA (ANCHO X ALTO X PROFUNDIDAD)</div>
            </div>
            <div class="box-info-style">
              <div class="value-style">{{ boxVolume }}</div>
              <div class="key-style">VOLUMEN DE LA CAJA</div>
            </div>
            <div class="box-info-style">
              <div class="value-style">{{ occupancy }}</div>
              <div class="key-style">OCUPACIÓN</div>
            </div>
            <div class="titulo">PRODUCTOS</div>
            <div class="box-items">
              <div
                v-for="(product, index) in products"
                :key="`${product.name}-${index}`"
                class="box-item"
                :style="{ borderLeftColor: itemColor(index) }">
                <div class="value-style">#{{ index + 1 }} {{ product.name }}</div>
                <div class="key-style">{{ productDimensions(product) }}</div>
              </div>
              <div v-if="!products.length" class="value-style">SIN PRODUCTOS</div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </el-dialog>
</template>

<script>
import * as BABYLON from 'babylonjs';
import { apiRequest } from '../../backend/vue-axios/axios';

const SCENE_SCALE = 1000;
const ITEM_COLORS = [
  [1, 0.4, 0.4],
  [0.3, 0.9, 0.8],
  [0.7, 0.5, 1],
  [1, 0.8, 0.3],
];

const num = (value) => {
  const parsed = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const pickDims = (source) => {
  if (!source) return null;
  const dims = source.dimensions || source.box || source;
  const width = num(dims.width);
  const height = num(dims.height);
  const depth = num(dims.depth !== undefined ? dims.depth : dims.length);
  if (width <= 0 || height <= 0 || depth <= 0) return null;
  return {
    width,
    height,
    depth,
    unit_of_measure: dims.unit_of_measure || dims.unitOfMeasure || 'm',
  };
};

const normalizePackage = (raw) => {
  const source = raw || {};
  const productsRaw = source.products || source.items || [];
  const products = productsRaw.map((product) => {
    const dims = pickDims(product);
    if (!dims) return null;
    const position = product.position || product;
    return {
      name: product.name || product.description || 'Producto',
      width: dims.width,
      height: dims.height,
      depth: dims.depth,
      x: num(position.x),
      y: num(position.y),
      z: num(position.z),
    };
  }).filter(Boolean);
  return {
    lpn: source.lpn || source.lpn_number || source.lpnNumber || '',
    suborder: source.suborder || source.suborder_id || '',
    box: pickDims(source.box || source.dimensions),
    products,
  };
};

export default {
  name: 'TracingSuborderBox',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    suborderId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      error: '',
      packages: [],
      rawResponse: null,
      selectedIndex: 0,
      engineReady: false,
    };
  },
  computed: {
    currentPackage() {
      return this.packages[this.selectedIndex] || {};
    },
    rawJson() {
      return JSON.stringify(this.rawResponse, null, 2);
    },
    products() {
      return this.currentPackage.products || [];
    },
    boxSize() {
      const { box } = this.currentPackage;
      if (box) return box;
      if (!this.products.length) return null;
      // algunas respuestas no traen caja: se usa la envolvente de los productos
      return {
        width: Math.max(...this.products.map(p => p.x + p.width)),
        height: Math.max(...this.products.map(p => p.y + p.height)),
        depth: Math.max(...this.products.map(p => p.z + p.depth)),
        unit_of_measure: 'm',
      };
    },
    boxDimensions() {
      const box = this.boxSize;
      if (!box) return '---';
      const unit = box.unit_of_measure || 'm';
      return `${this.round(box.width)} x ${this.round(box.height)} x ${this.round(box.depth)} ${unit}`;
    },
    boxVolume() {
      const box = this.boxSize;
      if (!box) return '---';
      const unit = box.unit_of_measure || 'm';
      return `${(box.width * box.height * box.depth).toFixed(4)} ${unit}3`;
    },
    occupancy() {
      const box = this.boxSize;
      if (!box) return '---';
      const total = box.width * box.height * box.depth;
      if (!total) return '---';
      const used = this.products
        .reduce((acc, p) => acc + (p.width * p.height * p.depth), 0);
      return `${((used / total) * 100).toFixed(1)}%`;
    },
  },
  methods: {
    round(value) {
      return Number(value || 0).toFixed(3);
    },
    productDimensions(product) {
      return `${this.round(product.width)} x ${this.round(product.height)} x ${this.round(product.depth)}`;
    },
    itemColor(index) {
      const color = ITEM_COLORS[index % ITEM_COLORS.length];
      return `rgb(${color.map(c => Math.round(c * 255)).join(',')})`;
    },
    onOpen() {
      this.error = '';
      this.packages = [];
      this.rawResponse = null;
      this.selectedIndex = 0;
      this.getBox();
    },
    onOpened() {
      this.initEngine().then(() => {
        this.renderPackage();
        // el-dialog termina su transición antes de que el canvas tenga tamaño final
        window.requestAnimationFrame(() => {
          if (this.$options.engine) this.$options.engine.resize();
        });
      });
    },
    onClosed() {
      this.disposeEngine();
      this.packages = [];
    },
    getBox() {
      if (!this.suborderId) {
        this.error = 'No se encontró la sub orden asociada';
        return;
      }
      this.loading = true;
      apiRequest({
        module: 'planning',
        service: 'get_suborders_box',
        params: {
          suborders: [Number(this.suborderId)],
          with_splits: false,
        },
      }).then((res) => {
        const body = res.data;
        const list = Array.isArray(body) ? body : (body && (body.data || body.result));
        this.rawResponse = body;
        this.packages = (Array.isArray(list) ? list : []).map(normalizePackage);
        if (!this.packages.length) {
          this.error = 'No hay información de paquete para esta sub orden';
        } else if (!this.boxSize) {
          this.error = 'El paquete no tiene dimensiones para visualizar';
        }
        this.loading = false;
        this.$nextTick(() => {
          this.initEngine().then(() => this.renderPackage());
        });
      }).catch(() => {
        this.loading = false;
        this.error = 'Error al cargar la información del paquete';
      });
    },
    initEngine() {
      if (this.engineReady) return Promise.resolve();
      const { canvas } = this.$refs;
      if (!canvas) return Promise.resolve();
      try {
        this.$options.engine = new BABYLON.Engine(canvas, true);
        this.$options.scene = new BABYLON.Scene(this.$options.engine);
        this.$options.scene.clearColor = new BABYLON.Color4(0.13, 0.14, 0.16, 1);
        this.$options.camera = new BABYLON.ArcRotateCamera(
          'camera',
          -Math.PI / 3,
          Math.PI / 3,
          500,
          BABYLON.Vector3.Zero(),
          this.$options.scene,
        );
        this.$options.camera.attachControl(canvas, true);
        this.$options.camera.wheelDeltaPercentage = 0.02;
        this.$options.camera.pinchDeltaPercentage = 0.02;
        const light = new BABYLON.HemisphericLight('light', new BABYLON.Vector3(0, 1, 0), this.$options.scene);
        light.intensity = 0.85;
        light.groundColor = new BABYLON.Color3(0.35, 0.35, 0.4);
        const dirLight = new BABYLON.DirectionalLight('dirLight', new BABYLON.Vector3(-0.6, -1, -0.4), this.$options.scene);
        dirLight.intensity = 0.6;
        this.$options.engine.runRenderLoop(() => this.$options.scene.render());
        this.$options.onResize = () => this.$options.engine.resize();
        window.addEventListener('resize', this.$options.onResize);
        this.engineReady = true;
        return Promise.resolve();
      } catch (err) {
        this.error = err.message;
        return Promise.reject(err);
      }
    },
    clearScene() {
      const { scene } = this.$options;
      if (!scene) return;
      [...scene.meshes].forEach(mesh => mesh.dispose());
      [...scene.materials].forEach(material => material.dispose());
    },
    renderPackage() {
      const { scene } = this.$options;
      const box = this.boxSize;
      if (!BABYLON || !scene || !box) return;
      this.clearScene();
      const boxMesh = BABYLON.MeshBuilder.CreateBox('box', {
        width: box.width * SCENE_SCALE,
        height: box.height * SCENE_SCALE,
        depth: box.depth * SCENE_SCALE,
        sideOrientation: BABYLON.Mesh.BACKSIDE,
      }, scene);
      const boxMat = new BABYLON.StandardMaterial('boxMat', scene);
      boxMat.diffuseColor = new BABYLON.Color3(0.55, 0.42, 0.25);
      boxMat.specularColor = new BABYLON.Color3(0, 0, 0);
      boxMat.alpha = 0.18;
      boxMesh.material = boxMat;
      boxMesh.isPickable = false;
      boxMesh.position.x = (box.width * SCENE_SCALE) / 2;
      boxMesh.position.y = (box.height * SCENE_SCALE) / 2;
      boxMesh.position.z = (box.depth * SCENE_SCALE) / 2;
      boxMesh.enableEdgesRendering();
      boxMesh.edgesWidth = 6;
      boxMesh.edgesColor = new BABYLON.Color4(1, 1, 1, 0.9);

      this.products.forEach((product, index) => {
        const itemMesh = BABYLON.MeshBuilder.CreateBox(`item${index}`, {
          width: Math.max(product.width * SCENE_SCALE, 1),
          height: Math.max(product.height * SCENE_SCALE, 1),
          depth: Math.max(product.depth * SCENE_SCALE, 1),
        }, scene);
        const itemMat = new BABYLON.StandardMaterial(`itemMat${index}`, scene);
        const color = ITEM_COLORS[index % ITEM_COLORS.length];
        itemMat.diffuseColor = new BABYLON.Color3(color[0], color[1], color[2]);
        itemMat.specularColor = new BABYLON.Color3(0.1, 0.1, 0.1);
        itemMesh.material = itemMat;
        itemMesh.position.x = (product.x + (product.width / 2)) * SCENE_SCALE;
        itemMesh.position.y = (product.y + (product.height / 2)) * SCENE_SCALE;
        itemMesh.position.z = (product.z + (product.depth / 2)) * SCENE_SCALE;
        itemMesh.enableEdgesRendering();
        itemMesh.edgesWidth = 3;
        itemMesh.edgesColor = new BABYLON.Color4(0.1, 0.1, 0.1, 0.8);
      });

      const diagonal = Math.sqrt(
        (box.width ** 2) + (box.height ** 2) + (box.depth ** 2),
      ) * SCENE_SCALE;
      this.$options.camera.setTarget(new BABYLON.Vector3(
        (box.width * SCENE_SCALE) / 2,
        (box.height * SCENE_SCALE) / 2,
        (box.depth * SCENE_SCALE) / 2,
      ));
      this.$options.camera.radius = diagonal * 1.9;
      this.$options.camera.lowerRadiusLimit = diagonal * 0.4;
      this.$options.camera.upperRadiusLimit = diagonal * 8;
      this.$options.camera.minZ = Math.max(diagonal / 500, 0.1);
      this.$options.camera.maxZ = diagonal * 50;
      this.$options.engine.resize();
    },
    disposeEngine() {
      if (this.$options.onResize) {
        window.removeEventListener('resize', this.$options.onResize);
        this.$options.onResize = null;
      }
      if (this.$options.engine) {
        this.$options.engine.dispose();
        this.$options.engine = null;
        this.$options.scene = null;
        this.$options.camera = null;
      }
      this.engineReady = false;
    },
  },
  beforeDestroy() {
    this.disposeEngine();
  },
};
</script>

<style scoped>
  .box-viewer {
    min-height: 420px;
  }
  .box-selector {
    margin-bottom: 1em;
  }
  .box-content {
    display: flex;
    flex-wrap: wrap;
  }
  .box-canvas {
    display: block;
    height: 460px;
    background-color: #22242A;
    border-radius: 4px;
    outline: none;
    touch-action: none;
    width: 100%;
  }
  .box-canvas-wrapper {
    flex: 1 1 380px;
    min-width: 320px;
  }
  .box-hint {
    color: #B0B0B0;
    font-size: 11px;
    margin-top: 6px;
  }
  .box-panel {
    flex: 0 0 280px;
    padding-left: 1.5em;
    box-sizing: border-box;
  }
  .box-items {
    max-height: 240px;
    overflow-y: auto;
  }
  .box-item {
    border-left: 4px solid #AAD500;
    padding-left: 8px;
    margin-bottom: 10px;
  }
  .box-error {
    color: #F56C6C;
    font-size: 14px;
    padding: 2em 0;
    text-align: center;
  }
  .box-raw {
    background-color: #F5F5F5;
    color: #606266;
    font-size: 11px;
    margin: 1em auto 0;
    max-height: 260px;
    max-width: 600px;
    overflow: auto;
    padding: 10px;
    text-align: left;
  }
  .titulo {
    font-size: 17px;
    text-align: left;
    margin-bottom: 1em;
    color: #9B9B9B;
  }
  .value-style {
    font-weight: 300;
    font-size: 14px;
    text-transform: uppercase;
    color: #707070;
  }
  .key-style {
    font-weight: 500;
    font-size: 10px;
    color: #AAD500;
    margin-bottom: 1em;
  }
  .box-info-style {
    text-align: left;
  }
</style>
